import { ArtStyle, CreativityLevel, PrintProduct } from './types.js';

// =============================================================
// Client-safe/shared constants (safe to import in the browser)
// =============================================================

/** DOM event used to sync currency across hooks/components. */
export const CURRENCY_CHANGED_EVENT = 'homeart:currency-changed';

/** SessionStorage key used to persist generation preferences (e.g. creativity). */
export const GENERATION_PREFERENCES_KEY = 'homeart_generation_preferences';

/** Default creativity level for prompt guidance. */
export const DEFAULT_CREATIVITY: CreativityLevel = 'balanced';

/** Max dimension (px) used when client-resizing uploads before sending to the API. */
export const IMAGE_RESIZE_MAX_SIZE = 1536;

export const ART_STYLES: ArtStyle[] = [
  {
    id: 'pop_art',
    name: 'Vibrant Pop Art',
    description: 'Bold colors and comic-style outlines inspired by Warhol and Lichtenstein.',
    productDescription:
      'This work of art is in the pop art style, featuring bold saturated colors, crisp comic-style outlines, and playful graphic contrast, inspired by Andy Warhol and Roy Lichtenstein.',
    promptModifier:
      'Transform this image into a vibrant Pop Art masterpiece inspired by Andy Warhol and Roy Lichtenstein. Critical: Maintain the exact perspective and structure of the house. Use bold, saturated, and contrasting colors. Apply heavy black outlines and Ben-Day dots for shading. IMPORTANT: Maintain consistent coloring for repeating architectural elements. For example, all window frames must be the same color, all shutters must be the same color, and all roof sections should match. Do not use random colors for identical building features.',
    thumbnailColor: 'bg-pink-600',
    hex: '#db2777',
    thumbnailPath: '/styles/thumbnails/popart.jpg',
    previewPath: '/styles/preview/popart.jpg',
  },
  {
    id: 'watercolor',
    name: 'Dreamy Watercolor',
    description: 'Soft edges, pastel colors, and artistic brush strokes.',
    productDescription:
      'This work of art is in the watercolor style, featuring soft washes, gentle gradients, and expressive brushwork, inspired by J.M.W. Turner and John Singer Sargent.',
    promptModifier:
      'Create a watercolor painting of this house. Critical: Keep the main structure and perspective of the building exactly as it is in the photo. Apply a dreamy, soft artistic style with pastel colors, wet-on-wet bleeding edges, and loose brush strokes. Avoid hard edges and photorealism.',
    thumbnailColor: 'bg-indigo-300',
    hex: '#a5b4fc',
    thumbnailPath: '/styles/thumbnails/watercolor.jpg',
    previewPath: '/styles/preview/watercolor.jpg',
  },
  {
    id: 'vector',
    name: 'Modern Vector',
    description: 'Flat colors, clean lines, and a minimalist aesthetic.',
    productDescription:
      'This work of art is in the style of modern vector graphics, featuring clean lines, simplified geometric shapes, and bold flat color fields, inspired by Saul Bass and Malika Favre.',
    promptModifier:
      'Transform this image into a modern flat vector illustration. Critical: Retain the building geometry and perspective. Use bold solid colors, clean thick outlines, and a somewhat minimalist style, all while preserving detail. Remove photographic noise and texture. Focus on geometric shapes and vibrant, solid colors.',
    thumbnailColor: 'bg-teal-500',
    hex: '#14b8a6',
    thumbnailPath: '/styles/thumbnails/vector.jpg',
    previewPath: '/styles/preview/vector.jpg',
  },
  {
    id: 'impressionist',
    name: 'Oil Impressionism',
    description: 'Thick brushwork inspired by Van Gogh and Monet.',
    productDescription:
      'This work of art is in the impressionist style, featuring lively brush strokes, luminous color, and a painterly sense of movement, inspired by Claude Monet and Vincent van Gogh.',
    promptModifier:
      'Repaint this image in the style of an impressionist oil painting. Critical: Maintain the house structure. Use visible, thick impasto brush strokes, vibrant light, and movement similar to Van Gogh or Monet. The sky should be expressive.',
    thumbnailColor: 'bg-yellow-600',
    hex: '#ca8a04',
    thumbnailPath: '/styles/thumbnails/impressionism.jpg',
    previewPath: '/styles/preview/impressionism.jpg',
  },
  {
    id: 'surrealism',
    name: 'Surrealism',
    description: 'Surrealist elements with dream-like qualities, inspired by Dali and Magritte.',
    productDescription:
      'This work of art is in the surrealist style, featuring dream-like atmosphere, unexpected visual juxtapositions, and imaginative details, inspired by Salvador Dalí and René Magritte.',
    promptModifier:
      'Recreate this image in the style of surrealism like Dali or Magritte. Critical: Maintain the house structure and perspective. You may add surrealist elements to the environment or background. Vibrant, detailed yet not photorealistic. It should be recognizable as a painting.',
    thumbnailColor: 'bg-pink-700',
    hex: '#be185d',
    thumbnailPath: '/styles/thumbnails/surrealism.jpg',
    previewPath: '/styles/preview/surrealism.jpg',
  },
  {
    id: 'pencil',
    name: 'Charcoal Sketch',
    description: 'Black and white hand-drawn charcoal sketch.',
    productDescription:
      'This work of art is in the charcoal sketch style, featuring high-contrast shading, textured strokes, and hand-drawn detail, inspired by John Ruskin and Käthe Kollwitz.',
    promptModifier:
      'Create a realistic charcoal sketch of this house on textured paper. Critical: strictly maintain the perspective and details of the architecture. High contrast black and white, rough shading, hand-drawn artistic feel.',
    thumbnailColor: 'bg-gray-700',
    hex: '#374151',
    thumbnailPath: '/styles/thumbnails/charcoal.jpg',
    previewPath: '/styles/preview/charcoal.jpg',
  },
  {
    id: 'blueprint',
    name: 'Architectural Blueprint',
    description: 'Classic technical drawing style with white lines on blueprint blue.',
    productDescription:
      'This work of art captures the elegance of architectural design in a classic blueprint style. It features precise white technical lines against a deep, rich cyanotype blue background, evoking a sense of history and craftsmanship.',
    promptModifier:
      'Create an architectural blueprint cyanotype of this house. Critical: Perfectly maintain the scale, perspective, and details of the building. The image should look like a technical drawing with clean white lines on a deep blue textured paper background. Include faint grid lines but no technical annotations. No photorealism, strictly line art. NO TEXT OR WRITING!',
    thumbnailColor: 'bg-blue-900',
    hex: '#1e3a8a',
    thumbnailPath: '/styles/thumbnails/blueprint.jpg',
    previewPath: '/styles/preview/blueprint.jpg',
  },
  {
    id: 'vintage_poster',
    name: 'Vintage Travel Poster',
    description: 'Nostalgic illustration style with flat colors and screen texture.',
    productDescription:
      'This work of art mimics the golden age of travel posters. It features flat, bold colors, simplified shapes, and a nostalgic screen-printed texture evocative of mid-century park posters. ',
    promptModifier:
      'Transform this image into a vintage National Park style travel poster. Critical: Keep the building shape and perspective intact. Use a limited color palette of flat, earthy tones (terracotta, sage, mustard). Apply a screen-print texture. Simplify the details into bold shapes. The sky should be a solid or gradient graphic color. NO TEXT OR WRITING!',
    thumbnailColor: 'bg-orange-700',
    hex: '#c2410c',
    thumbnailPath: '/styles/thumbnails/vintage_poster.jpg',
    previewPath: '/styles/preview/vintage_poster.jpg',
  },
  {
    id: 'ukiyo_e',
    name: 'Japanese Woodblock',
    description: 'Traditional Ukiyo-e style with nature-inspired colors.',
    productDescription:
      'This work of art is designed in the Ukiyo-e Japanese woodblock style. It features prominent outlines, flat perspective, and nature-inspired colors, paying homage to the traditional masters of the Edo period.',
    promptModifier:
      'Reimagine this house as a Japanese Ukiyo-e woodblock print. Critical: Maintain the structural integrity of the house. Use prominent outlines, flat shading, and a color palette of indigo, prussian blue, and soft reds. Stylize the clouds and trees to match traditional Japanese art (like Hokusai). Texture should resemble washi paper.',
    thumbnailColor: 'bg-indigo-800',
    hex: '#3730a3',
    thumbnailPath: '/styles/thumbnails/japanese_woodblock.jpg',
    previewPath: '/styles/preview/japanese_woodblock.jpg',
  },
  {
    id: 'pen_ink',
    name: 'Pen & Ink Stipple',
    description: 'Intricate black and white stippling and cross-hatching.',
    productDescription:
      'This work of art features the meticulous detail of traditional pen and ink illustration. Using stippling (dotwork) and cross-hatching, it creates deep contrast and texture, reminiscent of classic scientific illustrations.',
    promptModifier:
      'Create a highly detailed pen and ink illustration of this house. Critical: Strictly preserve the architecture. Use cross-hatching and stippling (dots) for shading. Black ink on slightly aged white paper. High contrast, focusing on the texture of the materials (brick, wood, siding).',
    thumbnailColor: 'bg-gray-900',
    hex: '#111827',
    thumbnailPath: '/styles/thumbnails/pen_ink.jpg',
    previewPath: '/styles/preview/pen_ink.jpg',
  },
  {
    id: 'synthwave',
    name: 'Neon Synthwave',
    description: 'Retro-futuristic aesthetic with glowing neons and purples.',
    productDescription:
      'This work of art transforms the scene into a retro-futuristic digital dreamscape. Featuring glowing neon accents, deep purples and cyans, and a grid-like aesthetic reminiscent of 1980s sci-fi.',
    promptModifier:
      'Transform this house into a 1980s Synthwave / Vaporwave aesthetic. Critical: Keep the house structure exactly as is. Change the time of day to night. Use a palette of neon pink, purple, and cyan. Add a subtle glowing grid to the ground. The windows should glow. The style is digital retro-futurism.',
    thumbnailColor: 'bg-purple-600',
    hex: '#9333ea',
    thumbnailPath: '/styles/thumbnails/synthwave.jpg',
    previewPath: '/styles/preview/synthwave.jpg',
  },
  {
    id: 'linocut',
    name: 'Linocut Print',
    description: 'Bold block print style with carved textures and negative space.',
    productDescription:
      'This work of art emulates a handcrafted linocut or block print. It features bold contrast, visible carving marks, and a rustic, tactile feel, emphasizing negative space and strong shadows.',
    promptModifier:
      'Create a linocut block print of this house. Critical: Maintain the main shapes of the house. The image should look like a stamped print with visible carving gouges and imperfections. Use high contrast, limited colors (perhaps just black and white or two-tone). Background should feel like textured paper.',
    thumbnailColor: 'bg-stone-600',
    hex: '#57534e',
    thumbnailPath: '/styles/thumbnails/linocut.jpg',
    previewPath: '/styles/preview/linocut.jpg',
  },
  {
    id: 'anime_scenic',
    name: 'Scenic Anime',
    description: 'Lush, painted backgrounds with vibrant clouds and light.',
    productDescription:
      'This work of art captures the breathtaking beauty of hand-painted animation backgrounds. Featuring lush greenery, towering cumulus clouds, and vibrant cinematic lighting, creating a sense of nostalgia and wonder.',
    promptModifier:
      'Paint this house in the style of a high-quality hand-painted anime background. Critical: Keep the house structure identical. Enhance the lighting to be vibrant and cinematic. The sky should feature large, detailed cumulus clouds. Foliage should be lush and painted with high detail. A sense of wonder and nostalgia.',
    thumbnailColor: 'bg-sky-400',
    hex: '#38bdf8',
    thumbnailPath: '/styles/thumbnails/scenic_anime.jpg',
    previewPath: '/styles/preview/scenic_anime.jpg',
  },
  {
    id: 'paper_cutout',
    name: 'Paper Cutout',
    description: 'Layered paper craft style with shadows and depth.',
    productDescription:
      'This work of art simulates a 3D paper cutout diorama. It utilizes layered shapes, cast shadows, and a clean, crafty aesthetic to give the image a unique sense of depth and playfulness.',
    promptModifier:
      'Recreate this image as a layered paper cutout craft. Critical: The perspective of the house must remain correct. The image should look like it is made of stacked layers of colored construction paper. Emphasize shadows between layers to create depth (shadowbox effect). Clean edges, no outlines.',
    thumbnailColor: 'bg-red-400',
    hex: '#f87171',
    thumbnailPath: '/styles/thumbnails/paper_cutout.jpg',
    previewPath: '/styles/preview/paper_cutout.jpg',
  },
  {
    id: 'art_deco',
    name: 'Art Deco',
    description: 'Geometric elegance, gold accents, and vertical symmetry.',
    productDescription:
      'This work of art features the glamorous Art Deco style of the 1920s. It focuses on geometric symmetry, streamlined forms, and metallic accents, creating a look of luxury and modernization.',
    promptModifier:
      'Stylize this house in the Art Deco graphic style. Critical: Maintain the building geometry but emphasize vertical lines and symmetry. Use a palette of black, gold, and emerald green. Simplify complex details into geometric patterns. The sky should have stylized sun rays or geometric clouds.',
    thumbnailColor: 'bg-emerald-800',
    hex: '#065f46',
    thumbnailPath: '/styles/thumbnails/art_deco.jpg',
    previewPath: '/styles/preview/art_deco.jpg',
  },
  {
    id: 'mid_century',
    name: 'Mid-Century Modern',
    description: 'Whimsical illustration style inspired by 1950s advertising.',
    productDescription:
      'This work of art captures the whimsical, offbeat charm of Mid-Century Modern illustration. Inspired by 1950s advertising, it features slightly offset colors, geometric trees, and playful textures.',
    promptModifier:
      'Illustrate this house in a Mid-Century Modern commercial art style (1950s). Critical: Keep the house recognizable. Use a retro color palette. Apply a slightly grainy texture. The style should be flattened but lively, with geometric shapes and stylized features.',
    thumbnailColor: 'bg-lime-600',
    hex: '#65a30d',
    thumbnailPath: '/styles/thumbnails/mid_century.jpg',
    previewPath: '/styles/preview/mid_century.jpg',
  },
];

export const PRINT_PRODUCTS: PrintProduct[] = [
  // --- POSTERS ---
  {
    id: 'poster_8x10',
    name: 'Matte Poster (8x10")',
    type: 'poster',
    price: 25.0,
    dimensions: '8x10 inches',
    width: 8,
    height: 10,
    orientation: 'portrait',
    printfulStoreProductId: 407886337,
    printfulVariantId: 4463,
    description: 'Museum-quality poster printed on high-quality matte paper sourced from Japan.',
    imagePath: '/products/matteposter.png',
  },
  {
    id: 'poster_10x8',
    name: 'Matte Poster (10x8")',
    type: 'poster',
    price: 25.0,
    dimensions: '10x8 inches',
    width: 10,
    height: 8,
    orientation: 'landscape',
    printfulStoreProductId: 408755584,
    printfulVariantId: 4463,
    description: 'Museum-quality poster printed on high-quality matte paper sourced from Japan.',
    imagePath: '/products/matteposter.png',
  },
  {
    id: 'poster_12x16',
    name: 'Matte Poster (12x16")',
    type: 'poster',
    price: 35.0,
    dimensions: '12x16 inches',
    width: 12,
    height: 16,
    orientation: 'portrait',
    printfulStoreProductId: 408143754,
    printfulVariantId: 1349,
    description: 'Museum-quality poster printed on high-quality matte paper sourced from Japan.',
    imagePath: '/products/matteposter.png',
  },
  {
    id: 'poster_16x12',
    name: 'Matte Poster (16x12")',
    type: 'poster',
    price: 35.0,
    dimensions: '16x12 inches',
    width: 16,
    height: 12,
    orientation: 'landscape',
    printfulStoreProductId: 408755868,
    printfulVariantId: 1349,
    description: 'Museum-quality poster printed on high-quality matte paper sourced from Japan.',
    imagePath: '/products/matteposter.png',
  },
  {
    id: 'poster_18x24',
    name: 'Matte Poster (18x24")',
    type: 'poster',
    price: 49.0,
    dimensions: '18x24 inches',
    width: 18,
    height: 24,
    orientation: 'portrait',
    printfulStoreProductId: 408144167,
    printfulVariantId: 1,
    description: 'Museum-quality poster printed on high-quality matte paper sourced from Japan.',
    imagePath: '/products/matteposter.png',
  },
  {
    id: 'poster_24x18',
    name: 'Matte Poster (24x18")',
    type: 'poster',
    price: 49.0,
    dimensions: '24x18 inches',
    width: 24,
    height: 18,
    orientation: 'landscape',
    printfulStoreProductId: 408756271,
    printfulVariantId: 1,
    description: 'Museum-quality poster printed on high-quality matte paper sourced from Japan.',
    imagePath: '/products/matteposter.png',
  },
  {
    id: 'poster_24x36',
    name: 'Matte Poster (24x36")',
    type: 'poster',
    price: 59.0,
    dimensions: '24x36 inches',
    width: 24,
    height: 36,
    orientation: 'portrait',
    printfulStoreProductId: 407887174,
    printfulVariantId: 2,
    description: 'Museum-quality poster printed on high-quality matte paper sourced from Japan.',
    imagePath: '/products/matteposter.png',
  },
  {
    id: 'poster_36x24',
    name: 'Matte Poster (36x24")',
    type: 'poster',
    price: 59.0,
    dimensions: '36x24 inches',
    width: 36,
    height: 24,
    orientation: 'landscape',
    printfulStoreProductId: 408756671,
    printfulVariantId: 2,
    description: 'Museum-quality poster printed on high-quality matte paper sourced from Japan.',
    imagePath: '/products/matteposter.png',
  },

  // --- FRAMED POSTERS ---
  {
    id: 'framed_poster_8x10',
    name: 'Framed Poster (8x10")',
    type: 'framed_poster',
    price: 55.0,
    dimensions: '8x10 inches',
    width: 8,
    height: 10,
    orientation: 'portrait',
    printfulStoreProductId: 408144516,
    printfulVariantId: 4651,
    printfulVariants: [
      { id: 4651, label: 'Black' },
      { id: 15021, label: 'Red Oak' },
    ],
    description: 'A polished look with a wood frame, ready to hang. Ayous wood .75″ thick frame from renewable forests. Acrylite front protector. Hanging hardware included.',
    imagePath: '/products/framed_poster.png',
  },
  {
    id: 'framed_poster_10x8',
    name: 'Framed Poster (10x8")',
    type: 'framed_poster',
    price: 55.0,
    dimensions: '10x8 inches',
    width: 10,
    height: 8,
    orientation: 'landscape',
    printfulStoreProductId: 408767825,
    printfulVariantId: 4651,
    printfulVariants: [
      { id: 4651, label: 'Black' },
      { id: 15021, label: 'Red Oak' },
    ],
    description: 'A polished look with a wood frame, ready to hang. Ayous wood .75″ thick frame from renewable forests. Acrylite front protector. Hanging hardware included.',
    imagePath: '/products/framed_poster.png',
  },
  {
    id: 'framed_poster_12x16',
    name: 'Framed Poster (12x16")',
    type: 'framed_poster',
    price: 75.0,
    dimensions: '12x16 inches',
    width: 12,
    height: 16,
    orientation: 'portrait',
    printfulStoreProductId: 408145532,
    printfulVariantId: 1350,
    printfulVariants: [
      { id: 1350, label: 'Black' },
      { id: 15025, label: 'Red Oak' },
    ],
    description: 'A polished look with a wood frame, ready to hang. Ayous wood .75″ thick frame from renewable forests. Acrylite front protector. Hanging hardware included.',
    imagePath: '/products/framed_poster.png',
  },
  {
    id: 'framed_poster_16x12',
    name: 'Framed Poster (16x12")',
    type: 'framed_poster',
    price: 75.0,
    dimensions: '16x12 inches',
    width: 16,
    height: 12,
    orientation: 'landscape',
    printfulStoreProductId: 408769433,
    printfulVariantId: 1350,
    printfulVariants: [
      { id: 1350, label: 'Black' },
      { id: 15025, label: 'Red Oak' },
    ],
    description: 'A polished look with a wood frame, ready to hang. Ayous wood .75″ thick frame from renewable forests. Acrylite front protector. Hanging hardware included.',
    imagePath: '/products/framed_poster.png',
  },
  {
    id: 'framed_poster_18x24',
    name: 'Framed Poster (18x24")',
    type: 'framed_poster',
    price: 99.0,
    dimensions: '18x24 inches',
    width: 18,
    height: 24,
    orientation: 'portrait',
    printfulStoreProductId: 408145691,
    printfulVariantId: 3,
    printfulVariants: [
      { id: 3, label: 'Black' },
      { id: 15031, label: 'Red Oak' },
    ],
    description: 'A polished look with a wood frame, ready to hang. Ayous wood .75″ thick frame from renewable forests. Acrylite front protector. Hanging hardware included.',
    imagePath: '/products/framed_poster.png',
  },
  {
    id: 'framed_poster_24x18',
    name: 'Framed Poster (24x18")',
    type: 'framed_poster',
    price: 99.0,
    dimensions: '24x18 inches',
    width: 24,
    height: 18,
    orientation: 'landscape',
    printfulStoreProductId: 408770098,
    printfulVariantId: 3,
    printfulVariants: [
      { id: 3, label: 'Black' },
      { id: 15031, label: 'Red Oak' },
    ],
    description: 'A polished look with a wood frame, ready to hang. Ayous wood .75″ thick frame from renewable forests. Acrylite front protector. Hanging hardware included.',
    imagePath: '/products/framed_poster.png',
  },

  // Square framed posters (Printful IDs to be synced)
  {
    id: 'framed_poster_12x12',
    name: 'Framed Poster (12x12")',
    type: 'framed_poster',
    price: 75.0,
    dimensions: '12x12 inches',
    width: 12,
    height: 12,
    orientation: 'square',
    printfulStoreProductId: 412698772,
    printfulVariantId: 4653,
    printfulVariants: [
      { id: 4653, label: 'Black' },
      { id: 15024, label: 'Red Oak' },
    ],
    description:
      'A polished look with a wood frame, ready to hang. Ayous wood .75″ thick frame from renewable forests. Acrylite front protector. Hanging hardware included.',
    imagePath: '/products/framed_poster.png',
  },
  {
    id: 'framed_poster_16x16',
    name: 'Framed Poster (16x16")',
    type: 'framed_poster',
    price: 85.0,
    dimensions: '16x16 inches',
    width: 16,
    height: 16,
    orientation: 'square',
    printfulStoreProductId: undefined,
    printfulVariantId: null,
    description:
      'A polished look with a wood frame, ready to hang. Ayous wood .75″ thick frame from renewable forests. Acrylite front protector. Hanging hardware included.',
    imagePath: '/products/framed_poster.png',
  },

  // --- FRAMED CANVAS ---
  {
    id: 'framed_canvas_8x10',
    name: 'Framed Canvas (8x10")',
    type: 'framed_canvas',
    price: 69.0,
    dimensions: '8x10 inches',
    width: 8,
    height: 10,
    orientation: 'portrait',
    printfulStoreProductId: 408863480,
    printfulVariantId: 17627,
    printfulVariants: [
      { id: 17627, label: 'Black' },
      { id: 17634, label: 'Brown' },
      { id: 17641, label: 'White' },
    ],
    description: 'Printed on textured and fade-resistant canvas, stretched on a quality pine tree frame. Rubber pads on back corners. Hanging hardware attached.',
    imagePath: '/products/canvas.png',
  },
  {
    id: 'framed_canvas_12x16',
    name: 'Framed Canvas (12x16")',
    type: 'framed_canvas',
    price: 89.0,
    dimensions: '12x16 inches',
    width: 12,
    height: 16,
    orientation: 'portrait',
    printfulStoreProductId: 408863901,
    printfulVariantId: 16035,
    printfulVariants: [
      { id: 16035, label: 'Black' },
      { id: 16041, label: 'Brown' },
      { id: 15696, label: 'White' },
    ],
    description: 'Printed on textured and fade-resistant canvas, stretched on a quality pine tree frame. Rubber pads on back corners. Hanging hardware attached.',
    imagePath: '/products/canvas.png',
  },
  {
    id: 'framed_canvas_16x12',
    name: 'Framed Canvas (16x12")',
    type: 'framed_canvas',
    price: 89.0,
    dimensions: '16x12 inches',
    width: 16,
    height: 12,
    orientation: 'landscape',
    printfulStoreProductId: 412741185,
    printfulVariantId: 16035,
    printfulVariants: [
      { id: 16035, label: 'Black' },
      { id: 16041, label: 'Brown' },
      { id: 15696, label: 'White' },
    ],
    description:
      'Printed on textured and fade-resistant canvas, stretched on a quality pine tree frame. Rubber pads on back corners. Hanging hardware attached.',
    imagePath: '/products/canvas.png',
  },
  {
    id: 'framed_canvas_10x8',
    name: 'Framed Canvas (10x8")',
    type: 'framed_canvas',
    price: 69.0,
    dimensions: '10x8 inches',
    width: 10,
    height: 8,
    orientation: 'landscape',
    printfulStoreProductId: 412695660,
    printfulVariantId: 17627,
    printfulVariants: [
      { id: 17627, label: 'Black' },
      { id: 17634, label: 'Brown' },
      { id: 17641, label: 'White' },
    ],
    description:
      'Printed on textured and fade-resistant canvas, stretched on a quality pine tree frame. Rubber pads on back corners. Hanging hardware attached.',
    imagePath: '/products/canvas.png',
  },
  {
    id: 'framed_canvas_16x16',
    name: 'Framed Canvas (16x16")',
    type: 'framed_canvas',
    price: 99.0,
    dimensions: '16x16 inches',
    width: 16,
    height: 16,
    orientation: 'square',
    printfulStoreProductId: 408864251,
    printfulVariantId: 16036,
    printfulVariants: [
      { id: 16036, label: 'Black' },
      { id: 16042, label: 'Brown' },
      { id: 15697, label: 'White' },
    ],
    description: 'Printed on textured and fade-resistant canvas, stretched on a quality pine tree frame. Rubber pads on back corners. Hanging hardware attached.',
    imagePath: '/products/canvas.png',
  },
  {
    id: 'framed_canvas_12x12',
    name: 'Framed Canvas (12x12")',
    type: 'framed_canvas',
    price: 89.0,
    dimensions: '12x12 inches',
    width: 12,
    height: 12,
    orientation: 'square',
    printfulStoreProductId: 412694515,
    printfulVariantId: 16034,
    printfulVariants: [
      { id: 16034, label: 'Black' },
      { id: 16040, label: 'Brown' },
      { id: 15695, label: 'White' },
    ],
    description:
      'Printed on textured and fade-resistant canvas, stretched on a quality pine tree frame. Rubber pads on back corners. Hanging hardware attached.',
    imagePath: '/products/canvas.png',
  },
  {
    id: 'framed_canvas_20x16',
    name: 'Framed Canvas (20x16")',
    type: 'framed_canvas',
    price: 109.0,
    dimensions: '20x16 inches',
    width: 20,
    height: 16,
    orientation: 'landscape',
    printfulStoreProductId: 408888826,
    printfulVariantId: 16037,
    printfulVariants: [
      { id: 16037, label: 'Black' },
      { id: 16043, label: 'Brown' },
      { id: 15698, label: 'White' },
    ],
    description: 'Printed on textured and fade-resistant canvas, stretched on a quality pine tree frame. Rubber pads on back corners. Hanging hardware attached.',
    imagePath: '/products/canvas.png',
  },
  {
    id: 'framed_canvas_16x20',
    name: 'Framed Canvas (16x20")',
    type: 'framed_canvas',
    price: 109.0,
    dimensions: '16x20 inches',
    width: 16,
    height: 20,
    orientation: 'portrait',
    printfulStoreProductId: undefined,
    printfulVariantId: null,
    description:
      'Printed on textured and fade-resistant canvas, stretched on a quality pine tree frame. Rubber pads on back corners. Hanging hardware attached.',
    imagePath: '/products/canvas.png',
  },
  {
    id: 'framed_canvas_18x24',
    name: 'Framed Canvas (18x24")',
    type: 'framed_canvas',
    price: 129.0,
    dimensions: '18x24 inches',
    width: 18,
    height: 24,
    orientation: 'portrait',
    printfulStoreProductId: undefined,
    printfulVariantId: null,
    description:
      'Printed on textured and fade-resistant canvas, stretched on a quality pine tree frame. Rubber pads on back corners. Hanging hardware attached.',
    imagePath: '/products/canvas.png',
  },
  {
    id: 'framed_canvas_24x18',
    name: 'Framed Canvas (24x18")',
    type: 'framed_canvas',
    price: 129.0,
    dimensions: '24x18 inches',
    width: 24,
    height: 18,
    orientation: 'landscape',
    printfulStoreProductId: undefined,
    printfulVariantId: null,
    description:
      'Printed on textured and fade-resistant canvas, stretched on a quality pine tree frame. Rubber pads on back corners. Hanging hardware attached.',
    imagePath: '/products/canvas.png',
  },
  {
    id: 'framed_canvas_24x36',
    name: 'Framed Canvas (24x36")',
    type: 'framed_canvas',
    price: 149.0,
    dimensions: '24x36 inches',
    width: 24,
    height: 36,
    orientation: 'portrait',
    printfulStoreProductId: undefined,
    printfulVariantId: null,
    description:
      'Printed on textured and fade-resistant canvas, stretched on a quality pine tree frame. Rubber pads on back corners. Hanging hardware attached.',
    imagePath: '/products/canvas.png',
  },
  {
    id: 'framed_canvas_36x24',
    name: 'Framed Canvas (36x24")',
    type: 'framed_canvas',
    price: 139.0,
    dimensions: '36x24 inches',
    width: 36,
    height: 24,
    orientation: 'landscape',
    printfulStoreProductId: 408889292,
    printfulVariantId: 16039,
    printfulVariants: [
      { id: 16039, label: 'Black' },
      { id: 16045, label: 'Brown' },
      { id: 15700, label: 'White' },
    ],
    description: 'Printed on textured and fade-resistant canvas, stretched on a quality pine tree frame. Rubber pads on back corners. Hanging hardware attached.',
    imagePath: '/products/canvas.png',
  },
];
